













































































































































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import {
  Table as DataTable,
  Pill,
  Dropdown,
  SvgIcon,
  LottieAnimation,
  Button,
} from "@brankas/web-ui";
import { namespace } from "vuex-class";
import {
  CoreProducts,
  DropdownOption,
  EnvironmentMode,
  GetTransactionsRequest,
  GetTransactionsRequestFilters,
  TableColumn,
  Transaction,
  TransactionFile,
  TransactionStatus,
  ClientInfo,
} from "@/types";
import * as FileUtils from "@/utils/file-utils";
import { FeatureToggles } from "@/utils/featureToggles";

import TransactionFilters, { FormFilters } from "./TransactionFilters.vue";
import DownloadTransactionsDialog from "./dialogs/DownloadTransactionsDialog.vue";

// TODO: Support pagination later

const clientModule = namespace("client");
const configModule = namespace("config");

@Component({
  components: {
    DataTable,
    Pill,
    Dropdown,
    SvgIcon,
    LottieAnimation,
    Button,
    TransactionFilters,
    DownloadTransactionsDialog,
  },
})
export default class TransactionList extends Vue {
  @Prop({ type: String, default: null }) clientId!: string | null;

  @configModule.Getter("getToggle") getToggle!: (
    key: FeatureToggles
  ) => boolean;

  @clientModule.Action("getClientTransactions") getClientTransactions!: (
    payload: GetTransactionsRequest
  ) => void;
  @clientModule.Action("downloadClientTransactions")
  downloadClientTransactions!: (
    payload: GetTransactionsRequest
  ) => TransactionFile | null;
  @clientModule.State("transactions") clientTransactions!: Transaction[];
  @clientModule.State("currentClient") clientInfo!: ClientInfo;

  loading = false;
  showFilters = false;
  showDownloadDialog = false;

  selectedEnvironment: EnvironmentMode = EnvironmentMode.SANDBOX;
  environmentFilters: DropdownOption[] = [
    {
      value: EnvironmentMode.SANDBOX,
      label: "Sandbox environment",
    },
    {
      value: EnvironmentMode.LIVE,
      label: "Live environment",
    },
  ];

  tableColumns: TableColumn[] = [
    {
      label: "CREATED",
      field: "date",
      sortable: true,
      width: "12rem",
      tdClass: "font-sans font-medium",
    },
    {
      label: "ID",
      field: "id",
      sortable: false,
      width: "20rem",
      tdClass: "font-sans font-medium text-sm",
    },
    {
      label: "SOURCE",
      field: "from",
      sortable: false,
      tdClass: "font-sans font-medium text-sm",
    },
    {
      label: "DESTINATION",
      field: "to",
      sortable: false,
      tdClass: "font-sans font-medium text-sm",
    },
    {
      label: "AMOUNT",
      field: "amount",
      sortable: false,
      tdClass: "font-sans font-medium",
    },
    {
      label: "STATUS",
      field: "status",
      sortable: false,
      thClass: "text-center",
      tdClass: "flex justify-center text-center",
    },
  ];

  productFilters: DropdownOption[] = [
    {
      value: CoreProducts.DIRECT,
      label: "Direct",
    },
    {
      value: CoreProducts.DISBURSE,
      label: "Disburse",
    },
    {
      value: CoreProducts.STATEMENT,
      label: "Statement",
    },
  ];

  selectedProduct: CoreProducts = CoreProducts.DIRECT;
  currentFilters: FormFilters = {};

  get toggleFilters(): boolean {
    return this.getToggle(FeatureToggles.TRANSACTIONS_FILTER);
  }

  @Watch("selectedEnvironment")
  watchEnvironment(value: EnvironmentMode) {
    this._fetchTransactions(value, this.selectedProduct);
  }

  @Watch("selectedProduct")
  watchSelectedProduct(value: CoreProducts) {
    this._fetchTransactions(this.selectedEnvironment, value);
  }

  @Watch("clientId", { immediate: true })
  watchClientId() {
    this._fetchTransactions(this.selectedEnvironment, this.selectedProduct);
  }

  _getStatusClass(status: TransactionStatus): string {
    const pendingStatuses = [
      TransactionStatus.PENDING,
      TransactionStatus.CREATED,
      TransactionStatus.AWAITING_APPROVAL,
    ];
    const errorStatuses = [
      TransactionStatus.LOGIN_ERROR,
      TransactionStatus.ERROR,
      TransactionStatus.REJECTED,
      TransactionStatus.FAILED,
      TransactionStatus.INVALID,
      TransactionStatus.CANCELED,
    ];
    const successStatuses = [
      TransactionStatus.COMPLETED,
      TransactionStatus.SUCCESS,
      TransactionStatus.APPROVED,
      TransactionStatus.PROCESSED,
    ];

    if (pendingStatuses.includes(status)) {
      return "status status-pending";
    }
    if (errorStatuses.includes(status)) {
      return "status status-error";
    }
    if (successStatuses.includes(status)) {
      return "status status-success";
    }

    return "status status-default";
  }

  _getAmountText(transaction: Transaction): string {
    const { amount, currency } = transaction;
    if (!amount) {
      return "N/A";
    }
    return `${currency} ${amount}`;
  }

  async _fetchTransactions(env: EnvironmentMode, product: CoreProducts) {
    if (!this.clientId) return;
    const filters = this._formatFilters();

    this.loading = true;
    try {
      await this.getClientTransactions({
        clientId: this.clientId,
        env,
        product,
        filters,
      });
    } catch (err) {
      console.error(err);
    } finally {
      this.loading = false;
    }
  }

  _formatFilters(): GetTransactionsRequestFilters {
    const { accountNo, minAmount, maxAmount, fromDate, toDate } =
      this.currentFilters;
    const formattedFilters: GetTransactionsRequestFilters = {};

    if (accountNo) {
      formattedFilters.accountNo = accountNo;
    }

    if (minAmount) {
      formattedFilters.minAmount = Number(minAmount);
    }

    if (maxAmount) {
      formattedFilters.maxAmount = Number(maxAmount);
    }

    if (fromDate) {
      formattedFilters.fromDate = fromDate;
    }

    if (toDate) {
      formattedFilters.toDate = toDate;
    }
    return formattedFilters;
  }

  resetFilters() {
    this.currentFilters = {};
    this.showFilters = false;
    this.$nextTick(async () => {
      await this._fetchTransactions(
        this.selectedEnvironment,
        this.selectedProduct
      );
    });
  }

  handleFilters(filters: FormFilters) {
    this.currentFilters = filters;
    this.$nextTick(async () => {
      await this._fetchTransactions(
        this.selectedEnvironment,
        this.selectedProduct
      );
    });
  }

  async handleDownloadTransactions() {
    if (!this.clientId) return;

    const filters = this._formatFilters();
    const response = await this.downloadClientTransactions({
      clientId: this.clientId,
      env: this.selectedEnvironment,
      product: this.selectedProduct,
      filters,
    });

    if (!response) {
      return;
    }
    this.showDownloadDialog = true;

    const { name, content } = response;
    const csvFile = FileUtils.base64toCSVFile(content);

    FileUtils.downloadBlob(csvFile, name);
  }
}
